import React, { useCallback, useContext, useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString, Point } from "geojson";
import rbush from "geojson-rbush";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import { LayerIds } from "fond/layers";
import { MapContext } from "fond/map/MapProvider";
import {
  DeleteButton,
  LineStringButton,
  PointButton,
  PolygonSelectButton,
  RedoButton,
  SelectButton,
  SnappingButton,
  ToolbarConfig,
  UndoButton,
} from "fond/map/Toolbar";
import { isPoint } from "fond/types/geojson";
import { useAppSelector } from "fond/utils/hooks";

import directSelect from "./directSelect";
import drawLineString from "./drawLineString";
import drawPoint from "./drawPoint";
import { isValid } from "./helper";
import simpleSelect from "./simpleSelectUG";
import ugDrawStyles from "./ugDrawStyles";

/**
 * Default drawing tools when drawing any feature type
 */
const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [
      <SelectButton key="select" />,
      <PolygonSelectButton key="selectPolygon" />,
      <PolygonSelectButton key="selectLasso" selectionMode="lasso" />,
    ],
  },
  {
    id: "drawTools",
    type: "action",
    items: [<PointButton key="drawPoint" />, <LineStringButton key="drawLineString" />],
  },
  {
    id: "snappingTools",
    type: "action",
    items: [<SnappingButton key="snap" />],
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const modes = {
  simple_select: simpleSelect,
  direct_select: directSelect,
  draw_point: drawPoint,
  draw_line_string: drawLineString,
  passing_draw_polygon: passingDrawMode,
};

const sources = [{ source: LayerIds.inStreet }, { source: LayerIds.inStreetPoints }];

/**
 * Component that handles the drawing of Underground paths and points within FOND Designer.
 */
const UndergroundHandler: React.FC = () => {
  const { data } = useAppSelector((state) => state.project.projects[state.project.projectId]);
  const { drawControl } = useContext(MapContext);

  // The initial features are derived from the data layer for underground paths & points which
  // are then validated based on their connectivity.
  const initialFeatures: Feature<LineString | Point>[] = useMemo(() => {
    const tree = rbush();
    tree.load([
      ...((data?.layers[LayerIds.inStreet].features || []) as Feature<LineString>[]),
      ...((data?.layers[LayerIds.inStreetPoints].features || []) as Feature<Point>[]),
    ]);

    return tree.all().features.map((f) => {
      if (f.properties) f.properties[`${DRAWING_CONSTANTS.Valid}`] = String(isValid(f, tree));
      return f;
    });
  }, [data]);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        allLayerIds: [LayerIds.inStreet, LayerIds.inStreetPoints],
        editingLayerIds: [LayerIds.inStreet],
        snap: true,
      }) as MapboxDrawOptions,
    []
  );

  /**
   * Determines the initial properties for any new feature being drawn.
   */
  const getFeatureProperties = useCallback(
    (feature: Feature<LineString | Point>) => {
      const tree = rbush();
      tree.load(drawControl?.current.getAll() || []);

      return {
        layerId: isPoint(feature) ? LayerIds.inStreetPoints : LayerIds.inStreet,
        [DRAWING_CONSTANTS.Valid]: drawControl && isValid(feature, tree).toString(),
      };
    },
    [drawControl]
  );

  return (
    <DrawHandler
      startingMode={initialFeatures.length > 0 ? "simple_select" : "draw_line_string"}
      initialFeatures={initialFeatures}
      sources={sources}
      styles={ugDrawStyles}
      config={toolbarConfig}
      getFeatureProperties={getFeatureProperties}
      options={options}
      modes={modes}
    />
  );
};

export default UndergroundHandler;
